import { useEffect, useRef } from 'preact/hooks';
import { useAppDispatch } from './index';
import { mergeOffer, mergeRows, setRows, mergeSelections, fetchOffers } from '../store/offersSlice';

const REVERB_HOST = import.meta.env.VITE_REVERB_HOST || 'localhost';
const REVERB_PORT = import.meta.env.VITE_REVERB_PORT || '8080';

/**
 * Connects to Laravel Reverb WebSocket for real-time updates on the given offer.
 * Uses native WebSocket — no Pusher/Echo dependency needed.
 */
export function useWebSocket(offerCode: number | null) {
    const dispatch = useAppDispatch();
    const wsRef = useRef<WebSocket | null>(null);

    useEffect(() => {
        if (!offerCode) {
            // Close existing connection when no offer selected
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            return;
        }

        const channel = `offer.${offerCode}`;
        const wsUrl = `ws://${REVERB_HOST}:${REVERB_PORT}/app/offprice-local-key?protocol=7&client=js&version=8.4.0-rc2&flash=false`;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
            // Subscribe to the offer channel
            ws.send(JSON.stringify({
                event: 'pusher:subscribe',
                data: { channel },
            }));
        };

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                const eventName = msg.event;
                const data = msg.data ? JSON.parse(msg.data) : {};

                switch (eventName) {
                    case 'offer.updated':
                        if (data.offer) {
                            dispatch(mergeOffer(data.offer));
                        }
                        break;

                    case 'offer.rows.updated':
                        if (data.rows) {
                            dispatch(mergeRows(data.rows));
                        }
                        break;

                    case 'offer.rows.loaded':
                        if (data.rows) {
                            dispatch(setRows(data.rows));
                        }
                        break;

                    case 'offer.buyer.selection.updated':
                        if (data.selections) {
                            dispatch(mergeSelections(data.selections));
                        }
                        break;

                    case 'offer.status.changed':
                        // Refetch offers list to update sidebar
                        dispatch(fetchOffers(undefined));
                        break;

                    case 'pusher:subscription_succeeded':
                    case 'pusher:connection_established':
                    case 'pusher:pong':
                        // Expected Pusher protocol messages
                        break;

                    default:
                        break;
                }
            } catch {
                // Ignore parse errors from non-JSON messages
            }
        };

        ws.onerror = () => {
            // Silent — WebSocket errors are expected when Reverb is not running
        };

        // Keepalive ping every 30 seconds
        const pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ event: 'pusher:ping', data: {} }));
            }
        }, 30000);

        return () => {
            clearInterval(pingInterval);
            if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                // Unsubscribe before closing
                ws.send(JSON.stringify({
                    event: 'pusher:unsubscribe',
                    data: { channel },
                }));
                ws.close();
            }
            wsRef.current = null;
        };
    }, [offerCode, dispatch]);
}
